import throttle from 'lodash.throttle';
import React from 'react';

import { Notification } from '@vimeo/iris/components';

import { MESSAGE_CHAR_LIMIT, T, THROTTLE_TIME } from './constants';
import { ShareModal } from './ShareModal';
import { ShareModalWrapper } from './Styles';

interface Api<P> {
  execute(arg: P): Promise<void>;
}

export interface CommonShareProps {
  card?: React.ReactNode;
  link?: string;
  token: string;
  reCaptchaSiteKey: string;
  trackCopy: VoidFunction;
  trackSubmit?: Function;
  trackAddEmail?: VoidFunction;
  children?: (options: Options) => React.ReactNode;
  showModal?: boolean;
  onDismiss?: (isSuccess?: boolean) => void;
  onSuccess?: VoidFunction;
}

export interface ShareProps<S = Api<Payload>> extends CommonShareProps {
  loading: boolean;
  api: S;
}

export interface ClipShareProps extends CommonShareProps {
  clipId: number;
  unlistedHash?: string;
}

export interface AlbumShareProps extends CommonShareProps {
  albumId: number;
  hideShareByEmail?: boolean;
}

export interface JobPostShareProps extends CommonShareProps {
  jobId: number;
  marketplaceApiUrl: string;
  jwt: string;
}

export interface Payload {
  data: Data;
  onSuccess: VoidFunction;
  onError: VoidFunction;
}

export interface Data {
  user_emails: string;
  message: string;
  'g-recaptcha-response': string;
}

type Props = ShareProps;

export interface Options {
  openShareModal: VoidFunction;
}

interface State {
  enableSubmit: boolean;
  showModal: boolean;
  hasExceededCount: boolean;
  captchaPassed: boolean;
  hasError: boolean;
  exceededCount: number;
  captchaResToken: string;
  emails: string[];
  messageInputValue: string;
  closeModal: VoidFunction;
  removeEmail: (index: number) => void;
  handleSendBtnState: (val: string) => void;
}

export class Share extends React.Component<Props, State> {
  private handleMsgInputThrottled: (msg: string) => void;

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (prevState.showModal !== nextProps.showModal) {
      return {
        showModal: nextProps.showModal,
      };
    }
    return null;
  }

  public constructor(props: Props) {
    super(props);
    this.state = {
      enableSubmit: false,
      showModal: props.showModal ?? false,
      hasExceededCount: false,
      captchaPassed: false,
      hasError: false,
      exceededCount: 0,
      captchaResToken: '',
      emails: [],
      messageInputValue: '',
      removeEmail: () => undefined,
      closeModal: () => undefined,
      handleSendBtnState: () => undefined,
    };

    this.handleMsgInputThrottled = throttle(this.handleMessageInputValue, THROTTLE_TIME);
  }

  public render() {
    const { children } = this.props;
    const { hasError } = this.state;

    return (
      <ShareModalWrapper>
        {children
          ? children({
              openShareModal: this.openShareModal,
            })
          : null}
        <ShareModal
          {...this.props}
          {...this.state}
          removeEmail={this.removeEmail}
          closeModal={() => this.closeShareModal(false)}
          handleCaptcha={this.handleCaptcha}
          handleSubmit={this.handleSubmit}
          updateEmailArray={this.updateEmailArray}
          handleCaptchaExpire={this.handleCaptchaExpire}
          handleMessageInput={this.handleMessageInput}
        />
        <Notification
          status="negative"
          showing={hasError}
          onComplete={this.hideToast}
          content={T.ErrorMsg}
        />
      </ShareModalWrapper>
    );
  }

  private handleSubmit = (recaptchaToken: string) => {
    const payload = {
      data: {
        user_emails: this.state.emails.join(','),
        message: this.state.messageInputValue,
        'g-recaptcha-response': recaptchaToken,
      },
      onSuccess: () => {
        if (this.props.trackSubmit) {
          this.props.trackSubmit(this.state.messageInputValue);
        }
        this.closeShareModal(true);
      },
      onError: () => {
        this.setState({
          hasError: true,
        });
      },
    };

    this.props.api.execute(payload);
  };

  private handleMessageInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    // https://reactjs.org/docs/events.html#event-pooling
    event.persist();
    this.handleMsgInputThrottled(event.target.value);
  };

  private handleMessageInputValue = (msg: string) => {
    if (msg.length > MESSAGE_CHAR_LIMIT) {
      this.setState({
        exceededCount: msg.length - MESSAGE_CHAR_LIMIT,
      });
    }

    this.setState({
      messageInputValue: msg,
      hasExceededCount: msg.length > MESSAGE_CHAR_LIMIT,
    });
  };

  private hideToast = () => {
    this.setState({
      hasError: false,
    });
  };

  private handleCaptcha = (res: string) => {
    this.setState(
      {
        captchaResToken: res,
        captchaPassed: true,
      },
      this.handleSendBtnState,
    );
  };

  private handleCaptchaExpire = () => {
    this.setState(
      {
        captchaPassed: false,
      },
      this.handleSendBtnState,
    );
  };

  private closeShareModal = (isSuccess: boolean) => {
    const { grecaptcha } = window as any;

    if (grecaptcha) {
      grecaptcha.reset();
    }

    this.setState({
      showModal: false,
      captchaPassed: false,
      emails: [],
      captchaResToken: '',
      messageInputValue: '',
      hasExceededCount: false,
      enableSubmit: false,
    });
    if (isSuccess && this.props.onSuccess) {
      this.props.onSuccess();
      return;
    }
    if (this.props.onDismiss) {
      this.props.onDismiss(isSuccess);
    }
  };

  private openShareModal = () => {
    this.setState({
      showModal: true,
    });
  };

  private handleSendBtnState = () => {
    this.setState({
      enableSubmit: this.state.captchaPassed && this.state.emails.length > 0,
    });
  };

  private updateEmailArray = (email: string) => {
    this.setState(
      (prevState) => ({
        ...prevState,
        emails: [...prevState.emails, email],
      }),
      this.handleSendBtnState,
    );
  };

  private removeEmail = (index: number) => {
    this.setState(
      (prevState) => ({
        ...prevState,
        emails: prevState.emails.filter((_, i) => i !== index),
      }),
      this.handleSendBtnState,
    );
  };
}
